import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Button,
	Checkbox,
	ChevDownIcon,
	ChevUpIcon,
	Dialog,
	Modal,
	ModuleHeader,
	SpinnerWithBackground,
	Text,
	Toggle,
} from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { GenericTable } from '../../components/GenericTable'
import { useFeatureFlag } from '../../hooks/useFeatureFlag'
import { useUnsavedChangesWarning } from '../../hooks/useUnsavedChangesWarning'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { ConfigureDealTeamModal } from '../dealteam/ConfigureDealTeamModal'
import { FlaggedWordsUpload } from './components/FlaggedWordsUpload'
import { REPORT_TYPES } from './reportTypes'

interface ModuleAdmin {
	id: string
	userId: string
	organizationId: string
	firstName: string
	lastName: string
	email: string
	externalId: string
	active: boolean
}

interface User {
	id: string
	organizationId: string
	email: string
	firstName: string
	lastName: string
	externalId: string
	active: boolean
}

type SearchInput = {
	search?: string
	page?: number
	perPage?: number
	sortBy?: 'email' | 'firstName' | 'lastName' | 'externalId'
	order?: 'asc' | 'desc'
}

// Combined Module Toggle with Admin Section Component
// Typed against eNPS (which has the widest schema with customFields).
// Modules without those fields (e.g. DealTeam) are cast at the call site.
type ModuleAdminSearchQuery = typeof trpc.admin.enps.searchModuleAdmins
type ModuleAdminSearchPossibleQuery = typeof trpc.admin.enps.searchPossibleModuleAdmins
type ModuleAdminAddMutation = typeof trpc.admin.enps.addModuleAdmins
type ModuleAdminDeleteMutation = typeof trpc.admin.enps.deleteModuleAdmins

interface ModuleToggleWithAdminsProps {
	title: string
	description: string
	organizationId: string
	enabled: boolean
	onToggleChange: (checked: boolean) => void
	isExpanded: boolean
	onExpandToggle: () => void
	searchAdminsQuery: ModuleAdminSearchQuery
	searchPossibleAdminsQuery: ModuleAdminSearchPossibleQuery
	addAdminsMutation: ModuleAdminAddMutation
	deleteAdminsMutation: ModuleAdminDeleteMutation
	queryKeyPrefix: string
	adminLabel?: string
	showToggle?: boolean
	emptyStateDescription?: string
	modalDescription?: string
	className?: string
}

function ModuleToggleWithAdmins({
	title,
	description,
	organizationId,
	enabled,
	onToggleChange,
	isExpanded,
	onExpandToggle,
	searchAdminsQuery,
	searchPossibleAdminsQuery,
	addAdminsMutation,
	deleteAdminsMutation,
	queryKeyPrefix,
	adminLabel = 'Module Admins',
	showToggle = true,
	emptyStateDescription,
	modalDescription,
	className,
}: ModuleToggleWithAdminsProps) {
	const [isModalOpen, setIsModalOpen] = useState(false)
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
	const [selectedUsers, setSelectedUsers] = useState<User[]>([])
	const [selectedAdminsToDelete, setSelectedAdminsToDelete] = useState<ModuleAdmin[]>([])
	const queryClient = useQueryClient()
	const { showToast } = useToast()

	const invalidateAdminQueries = () => {
		// Invalidate both the admin list and possible admins list
		// queryKeyPrefix is like "admin.enps.searchModuleAdmins" or "admin.enps.searchModuleViewerAdmins"
		const pathParts = queryKeyPrefix.split('.')
		const modulePrefix = pathParts.slice(0, 2) // e.g., ["admin", "enps"]
		const searchName = pathParts[2] // e.g., "searchModuleAdmins" or "searchModuleViewerAdmins"
		const possibleSearchName = searchName.replace('search', 'searchPossible')

		queryClient.invalidateQueries({
			predicate: (query) => {
				const key = query.queryKey
				if (Array.isArray(key) && Array.isArray(key[0])) {
					const queryPath = key[0] as string[]
					return (
						queryPath[0] === modulePrefix[0] &&
						queryPath[1] === modulePrefix[1] &&
						(queryPath[2] === searchName || queryPath[2] === possibleSearchName)
					)
				}
				return false
			},
			refetchType: 'all',
		})
	}

	const addMutation = useMutation({
		...addAdminsMutation.mutationOptions(),
		onSuccess: () => {
			showToast(`User(s) added as ${title} admin(s)`)
			setIsModalOpen(false)
			setSelectedUsers([])
		},
		onError: () => {
			showToast(`Failed to add ${title} admin(s)`, 'error')
		},
		onSettled: () => {
			invalidateAdminQueries()
		},
	})

	const deleteMutation = useMutation({
		...deleteAdminsMutation.mutationOptions(),
		onSuccess: () => {
			showToast(`${title} admin(s) removed`)
			setSelectedAdminsToDelete([])
			setIsDeleteDialogOpen(false)
		},
		onError: () => {
			showToast(`Failed to remove ${title} admin(s)`, 'error')
		},
		onSettled: () => {
			invalidateAdminQueries()
		},
	})

	const handleAddSelected = () => {
		addMutation.mutate({
			organizationId,
			userIds: selectedUsers.map((user) => user.id),
		})
	}

	const handleDeleteSelected = () => {
		deleteMutation.mutate({
			organizationId,
			userIds: selectedAdminsToDelete.map((admin) => admin.userId),
		})
	}

	return (
		<div className={className || 'bg-neutral-50 border border-neutral-200 rounded-lg overflow-hidden'}>
			{/* Toggle Row */}
			{showToggle && (
				<div className='flex items-center justify-between p-4'>
					<div className='flex-1 pr-4'>
						<Text variant='medium' weight='semibold'>
							{title}
						</Text>
						<Text variant='small' className='text-neutral-600 mt-1'>
							{description}
						</Text>
					</div>
					<Toggle
						checked={enabled}
						onChange={(e) => onToggleChange(e.target.checked)}
						aria-label={`Enable ${title}`}
						size='medium'
					/>
				</div>
			)}

			{/* Admin Section - Only visible when enabled */}
			{enabled && (
				<>
					<button
						type='button'
						onClick={onExpandToggle}
						className={`w-full flex items-center justify-between px-4 py-3 bg-neutral-100 ${showToggle ? 'border-t' : ''} border-neutral-200 hover:bg-neutral-150 transition-colors`}>
						<Text variant='small' weight='semibold' className='text-neutral-700'>
							{adminLabel}
						</Text>
						{isExpanded ? (
							<ChevUpIcon className='w-4 h-4 text-neutral-500' />
						) : (
							<ChevDownIcon className='w-4 h-4 text-neutral-500' />
						)}
					</button>

					{isExpanded && (
						<div className='p-4 border-t border-neutral-200 bg-white'>
							<div className='flex justify-end gap-2 mb-4'>
								<Button variant='primary' onClick={() => setIsModalOpen(true)}>
									Add Admin
								</Button>
								<Button
									variant='destructive'
									onClick={() => setIsDeleteDialogOpen(true)}
									disabled={selectedAdminsToDelete.length === 0}>
									Remove Selected
								</Button>
							</div>

							<GenericTable<ModuleAdmin, SearchInput>
								queryOptionsGenerator={(options) => ({
									...searchAdminsQuery.queryOptions({
										...options,
										organizationId,
									}),
								})}
								selectable
								onSelectionChange={setSelectedAdminsToDelete}
								columns={[
									{
										field: 'firstName',
										label: 'Name',
										colSpan: 2,
										render: (admin) => (
											<Text variant='xsmall' weight='regular'>
												{`${admin.firstName} ${admin.lastName}`}
											</Text>
										),
									},
									{
										field: 'externalId',
										label: 'ID',
										colSpan: 1,
									},
									{
										field: 'email',
										label: 'Email',
										colSpan: 2,
									},
								]}
								emptyState={{
									title: 'No module admins',
									description:
										emptyStateDescription || `Add users who should be able to manage ${title} configurations`,
									actionLabel: 'Add Admin',
									onAction: () => setIsModalOpen(true),
								}}
							/>

							{/* Add Admin Modal */}
							<Modal
								isOpen={isModalOpen}
								onClose={() => setIsModalOpen(false)}
								title={`Add ${title} Module Admins`}
								buttons={[
									{
										children: 'Cancel',
										onClick: () => {
											setIsModalOpen(false)
											setSelectedUsers([])
										},
										variant: 'secondary',
									},
									{
										children: `Add ${selectedUsers.length} Admin${selectedUsers.length !== 1 ? 's' : ''}`,
										onClick: handleAddSelected,
										variant: 'primary',
										disabled: selectedUsers.length === 0 || addMutation.isPending,
									},
								]}>
								<div className='flex flex-col gap-4'>
									<Text variant='small' className='text-neutral-600'>
										{modalDescription ||
											`Select users to add as module administrators. Module admins can manage all ${title} configurations for this organization.`}
									</Text>

									<GenericTable<User, SearchInput>
										queryOptionsGenerator={(options) => ({
											...searchPossibleAdminsQuery.queryOptions({
												...options,
												organizationId,
											}),
										})}
										selectable
										onSelectionChange={setSelectedUsers}
										columns={[
											{
												field: 'firstName',
												label: 'Name',
												colSpan: 2,
												render: (user) => (
													<Text variant='xsmall' weight='regular'>
														{`${user.firstName} ${user.lastName}`}
													</Text>
												),
											},
											{
												field: 'externalId',
												label: 'ID',
												colSpan: 1,
											},
											{
												field: 'email',
												label: 'Email',
												colSpan: 2,
											},
										]}
										emptyState={{
											title: 'No users available',
											description: 'All eligible users are already module administrators',
										}}
									/>
								</div>
							</Modal>

							{/* Delete Confirmation Dialog */}
							<Dialog
								isOpen={isDeleteDialogOpen}
								onClose={() => setIsDeleteDialogOpen(false)}
								onConfirm={handleDeleteSelected}
								title={`Remove ${title} Module Admins`}
								message={`Are you sure you want to remove ${selectedAdminsToDelete.length} admin${selectedAdminsToDelete.length !== 1 ? 's' : ''}? They will lose the ability to manage ${title} configurations.`}
								type='delete'
								confirmLabel='Remove'
								isConfirmLoading={deleteMutation.isPending}
							/>
						</div>
					)}
				</>
			)}
		</div>
	)
}

export const ModuleSettingsPage = () => {
	const { organizationId } = useParams<{ organizationId: string }>()
	const [searchParams, setSearchParams] = useSearchParams()
	const toast = useToast()
	const queryClient = useQueryClient()
	const hasProcessedCallback = useRef(false)

	const exchangeCodeMutation = useMutation({
		...trpc.admin.integrations.crmConnection.exchangeCode.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.integrations.crmConnection.getByOrganization.queryKey({
					organizationId: organizationId as string,
				}),
			})
			toast.showToast('CRM connected successfully', 'info')
		},
		onError: (error) => {
			toast.showToast(`CRM connection failed: ${error.message}`, 'error')
		},
		onSettled: () => {
			setSearchParams({}, { replace: true })
		},
	})

	const { data: organization, isLoading } = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const [loopsEnabled, setLoopsEnabled] = useState(false)
	const [pecEnabled, setPecEnabled] = useState(false)
	const [enpsEnabled, setEnpsEnabled] = useState(false)
	const [dealTeamEnabled, setDealTeamEnabled] = useState(false)
	const [isDealTeamModalOpen, setIsDealTeamModalOpen] = useState(false)

	const dealteamModuleEnabled = useFeatureFlag('dealteam_module_enabled')

	const [disabledReportIds, setDisabledReportIds] = useState<string[]>([])

	const { data: savedDisabledReportIds } = useQuery({
		...trpc.admin.organizations.getDisabledStatusReportIds.queryOptions({
			organizationId: organizationId as string,
		}),
		enabled: !!organizationId,
	})

	const { mutate: saveDisabledReportIds, isPending: isSavingReports } = useMutation({
		...trpc.admin.organizations.setDisabledStatusReportIds.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.organizations.getDisabledStatusReportIds.queryKey({
					organizationId: organizationId as string,
				}),
			})
			toast.showToast('Status report settings updated successfully')
		},
		onError: () => {
			toast.showToast('Failed to update status report settings', 'error')
		},
	})

	useEffect(() => {
		if (savedDisabledReportIds !== undefined) {
			setDisabledReportIds(savedDisabledReportIds)
		}
	}, [savedDisabledReportIds])

	const handleReportToggle = (reportId: string, enabled: boolean) => {
		setDisabledReportIds((prev) => (enabled ? prev.filter((id) => id !== reportId) : [...prev, reportId]))
	}

	const handleSaveReports = () => {
		saveDisabledReportIds({ organizationId: organizationId as string, disabledIds: disabledReportIds })
	}

	const hasReportChanges =
		savedDisabledReportIds !== undefined &&
		JSON.stringify([...disabledReportIds].sort()) !== JSON.stringify([...savedDisabledReportIds].sort())

	// Expanded states for module admin sections
	const [enpsExpanded, setEnpsExpanded] = useState(false)
	const [loopsExpanded, setLoopsExpanded] = useState(false)
	const [pecExpanded, setPecExpanded] = useState(false)
	const [dealTeamExpanded, setDealTeamExpanded] = useState(false)

	// Expanded states for module viewer admin sections
	const [enpsViewerExpanded, setEnpsViewerExpanded] = useState(false)
	const [loopsViewerExpanded, setLoopsViewerExpanded] = useState(false)
	const [pecViewerExpanded, setPecViewerExpanded] = useState(false)

	// Sync local state when organization data loads
	useEffect(() => {
		if (organization) {
			setLoopsEnabled(organization.loopModuleEnabled)
			setPecEnabled(organization.pecModuleEnabled)
			setEnpsEnabled(organization.enpsModuleEnabled)
			setDealTeamEnabled(organization.dealTeamModuleEnabled)
		}
	}, [organization])

	// Handle OAuth callback for CRM connections
	useEffect(() => {
		const code = searchParams.get('code')
		const state = searchParams.get('state')
		const error = searchParams.get('error')
		const errorDescription = searchParams.get('error_description')
		const isCrmCallback = searchParams.get('crm_connection_callback')

		if (!isCrmCallback || hasProcessedCallback.current) {
			return
		}

		hasProcessedCallback.current = true

		if (error) {
			let userMessage = 'Failed to connect CRM'
			if (error === 'access_denied') {
				userMessage = 'Authorization was denied. Please try again if you want to connect.'
			} else if (errorDescription) {
				userMessage = `CRM error: ${errorDescription}`
			}
			toast.showToast(userMessage, 'error')
			setSearchParams({}, { replace: true })
			return
		}

		if (code && state) {
			const redirectUri = `${window.location.origin}${window.location.pathname}?crm_connection_callback=true`
			exchangeCodeMutation.mutate({ code, state, redirectUri })
			setIsDealTeamModalOpen(true) // Open the deal team modal to show status
			return
		}
	}, [searchParams, exchangeCodeMutation, toast, setSearchParams])

	const { mutate: editOrganization, isPending } = useMutation({
		...trpc.admin.organizations.edit.mutationOptions(),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ['admin', 'organizations', 'getById', { id: organizationId }],
			})
			toast.showToast('Module settings updated successfully')
		},
		onError: () => {
			toast.showToast('Failed to update module settings', 'error')
		},
	})

	const handleSave = () => {
		editOrganization({
			id: organizationId as string,
			loopModuleEnabled: loopsEnabled,
			pecModuleEnabled: pecEnabled,
			enpsModuleEnabled: enpsEnabled,
			dealTeamModuleEnabled: dealTeamEnabled,
		})
	}

	const hasChanges =
		loopsEnabled !== organization?.loopModuleEnabled ||
		pecEnabled !== organization?.pecModuleEnabled ||
		enpsEnabled !== organization?.enpsModuleEnabled ||
		dealTeamEnabled !== organization?.dealTeamModuleEnabled ||
		hasReportChanges

	const { isBlocked, confirmLeave, cancelLeave } = useUnsavedChangesWarning(hasChanges)

	if (isLoading) {
		return (
			<main>
				<SpinnerWithBackground backgroundClass='opacity-100' />
			</main>
		)
	}

	return (
		<main className='w-full p-6 flex flex-col gap-6'>
			<ModuleHeader
				breadcrumb={[
					{ label: 'Organizations', to: '/organizations' },
					{ label: organization?.name || '', to: `/organizations/${organizationId}` },
					{ label: 'Module Settings' },
				]}
				title='Module Settings'
				logo={{
					url: organization?.logo || '',
					name: organization?.name || '',
				}}
			/>

			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
				{/* Enable/Disable Modules Section */}
				<section className='bg-white border border-neutral-200 rounded-lg shadow-sm'>
					<div className='p-6 border-b border-neutral-200'>
						<Text variant='large' weight='semibold'>
							Enable/Disable Modules
						</Text>
						<Text variant='small' className='text-neutral-600 mt-1'>
							Control which modules are available to users in the client application.
						</Text>
					</div>

					<div className='p-6 space-y-4'>
						<div>
							<ModuleToggleWithAdmins
								title='Loops Module'
								description='Continuous feedback collection and iteration tracking.'
								organizationId={organizationId as string}
								enabled={loopsEnabled}
								onToggleChange={setLoopsEnabled}
								isExpanded={loopsExpanded}
								onExpandToggle={() => setLoopsExpanded(!loopsExpanded)}
								searchAdminsQuery={trpc.admin.loops.searchModuleAdmins}
								searchPossibleAdminsQuery={trpc.admin.loops.searchPossibleModuleAdmins}
								addAdminsMutation={trpc.admin.loops.addModuleAdmins}
								deleteAdminsMutation={trpc.admin.loops.deleteModuleAdmins}
								queryKeyPrefix='admin.loops.searchModuleAdmins'
								className={
									loopsEnabled ? 'bg-neutral-50 border border-neutral-200 rounded-t-lg overflow-hidden' : undefined
								}
							/>
							{loopsEnabled && (
								<ModuleToggleWithAdmins
									title='Loops Module'
									description=''
									organizationId={organizationId as string}
									enabled={loopsEnabled}
									onToggleChange={() => {}}
									isExpanded={loopsViewerExpanded}
									onExpandToggle={() => setLoopsViewerExpanded(!loopsViewerExpanded)}
									searchAdminsQuery={trpc.admin.loops.searchModuleViewerAdmins}
									searchPossibleAdminsQuery={trpc.admin.loops.searchPossibleModuleViewerAdmins}
									addAdminsMutation={trpc.admin.loops.addModuleViewerAdmins}
									deleteAdminsMutation={trpc.admin.loops.deleteModuleViewerAdmins}
									queryKeyPrefix='admin.loops.searchModuleViewerAdmins'
									adminLabel='Module Viewer Admins'
									showToggle={false}
									emptyStateDescription='Add users who can view results of Loops'
									modalDescription='Select users who can view results of Loops for this organization.'
									className='bg-neutral-50 border border-neutral-200 border-t-0 rounded-b-lg overflow-hidden'
								/>
							)}
						</div>

						<div>
							<ModuleToggleWithAdmins
								title='PEC Module'
								description='Performance Evaluation Cycle for comprehensive reviews.'
								organizationId={organizationId as string}
								enabled={pecEnabled}
								onToggleChange={setPecEnabled}
								isExpanded={pecExpanded}
								onExpandToggle={() => setPecExpanded(!pecExpanded)}
								searchAdminsQuery={trpc.admin.pec.searchModuleAdmins}
								searchPossibleAdminsQuery={trpc.admin.pec.searchPossibleModuleAdmins}
								addAdminsMutation={trpc.admin.pec.addModuleAdmins}
								deleteAdminsMutation={trpc.admin.pec.deleteModuleAdmins}
								queryKeyPrefix='admin.pec.searchModuleAdmins'
								className={
									pecEnabled ? 'bg-neutral-50 border border-neutral-200 rounded-t-lg overflow-hidden' : undefined
								}
							/>
							{pecEnabled && (
								<ModuleToggleWithAdmins
									title='PEC Module'
									description=''
									organizationId={organizationId as string}
									enabled={pecEnabled}
									onToggleChange={() => {}}
									isExpanded={pecViewerExpanded}
									onExpandToggle={() => setPecViewerExpanded(!pecViewerExpanded)}
									searchAdminsQuery={trpc.admin.pec.searchModuleViewerAdmins}
									searchPossibleAdminsQuery={trpc.admin.pec.searchPossibleModuleViewerAdmins}
									addAdminsMutation={trpc.admin.pec.addModuleViewerAdmins}
									deleteAdminsMutation={trpc.admin.pec.deleteModuleViewerAdmins}
									queryKeyPrefix='admin.pec.searchModuleViewerAdmins'
									adminLabel='Module Viewer Admins'
									showToggle={false}
									emptyStateDescription='Add users who can view results of PEC'
									modalDescription='Select users who can view results of PEC for this organization.'
									className='bg-neutral-50 border border-neutral-200 border-t-0 rounded-b-lg overflow-hidden'
								/>
							)}
						</div>

						<div>
							<ModuleToggleWithAdmins
								title='eNPS Module'
								description='Employee Net Promoter Score satisfaction surveys.'
								organizationId={organizationId as string}
								enabled={enpsEnabled}
								onToggleChange={setEnpsEnabled}
								isExpanded={enpsExpanded}
								onExpandToggle={() => setEnpsExpanded(!enpsExpanded)}
								searchAdminsQuery={trpc.admin.enps.searchModuleAdmins}
								searchPossibleAdminsQuery={trpc.admin.enps.searchPossibleModuleAdmins}
								addAdminsMutation={trpc.admin.enps.addModuleAdmins}
								deleteAdminsMutation={trpc.admin.enps.deleteModuleAdmins}
								queryKeyPrefix='admin.enps.searchModuleAdmins'
								className={
									enpsEnabled ? 'bg-neutral-50 border border-neutral-200 rounded-t-lg overflow-hidden' : undefined
								}
							/>
							{enpsEnabled && (
								<ModuleToggleWithAdmins
									title='eNPS Module'
									description=''
									organizationId={organizationId as string}
									enabled={enpsEnabled}
									onToggleChange={() => {}}
									isExpanded={enpsViewerExpanded}
									onExpandToggle={() => setEnpsViewerExpanded(!enpsViewerExpanded)}
									searchAdminsQuery={trpc.admin.enps.searchModuleViewerAdmins}
									searchPossibleAdminsQuery={trpc.admin.enps.searchPossibleModuleViewerAdmins}
									addAdminsMutation={trpc.admin.enps.addModuleViewerAdmins}
									deleteAdminsMutation={trpc.admin.enps.deleteModuleViewerAdmins}
									queryKeyPrefix='admin.enps.searchModuleViewerAdmins'
									adminLabel='Module Viewer Admins'
									showToggle={false}
									emptyStateDescription='Add users who can view results of eNPS'
									modalDescription='Select users who can view results of eNPS for this organization.'
									className='bg-neutral-50 border border-neutral-200 border-t-0 rounded-b-lg overflow-hidden'
								/>
							)}
						</div>

						{dealteamModuleEnabled && (
							<>
								<ModuleToggleWithAdmins
									title='DealTeam Module'
									description='Automated deal team cycle launches triggered by CRM events.'
									organizationId={organizationId as string}
									enabled={dealTeamEnabled}
									onToggleChange={setDealTeamEnabled}
									isExpanded={dealTeamExpanded}
									onExpandToggle={() => setDealTeamExpanded(!dealTeamExpanded)}
									searchAdminsQuery={trpc.admin.dealTeam.searchModuleAdmins}
									searchPossibleAdminsQuery={
										trpc.admin.dealTeam.searchPossibleModuleAdmins as unknown as ModuleAdminSearchPossibleQuery
									}
									addAdminsMutation={trpc.admin.dealTeam.addModuleAdmins}
									deleteAdminsMutation={trpc.admin.dealTeam.deleteModuleAdmins}
									queryKeyPrefix='admin.dealTeam.searchModuleAdmins'
								/>
								{dealTeamEnabled && (
									<div className='ml-4 mt-2'>
										<Button variant='secondary' size='small' onClick={() => setIsDealTeamModalOpen(true)}>
											View Configurations
										</Button>
									</div>
								)}
							</>
						)}
					</div>

					<div className='p-6 border-t border-neutral-200 flex justify-end gap-3'>
						<Button
							variant='secondary'
							onClick={() => {
								setLoopsEnabled(organization?.loopModuleEnabled ?? false)
								setPecEnabled(organization?.pecModuleEnabled ?? false)
								setEnpsEnabled(organization?.enpsModuleEnabled ?? false)
								setDealTeamEnabled(organization?.dealTeamModuleEnabled ?? false)
							}}
							disabled={!hasChanges}>
							Reset
						</Button>
						<Button variant='primary' onClick={handleSave} disabled={!hasChanges || isPending}>
							{isPending ? 'Saving...' : 'Save Changes'}
						</Button>
					</div>
				</section>

				{/* Important Notes Section */}
				<section className='bg-primary-50 border border-primary-200 rounded-lg p-6 h-fit'>
					<Text variant='medium' weight='semibold' className='text-primary-900 mb-3'>
						Important Notes
					</Text>
					<ul className='space-y-2'>
						<li className='flex items-start gap-2'>
							<span className='text-primary-600 mt-0.5'>•</span>
							<Text variant='small' className='text-primary-800'>
								Changes take effect immediately for all users in the organization
							</Text>
						</li>
						<li className='flex items-start gap-2'>
							<span className='text-primary-600 mt-0.5'>•</span>
							<Text variant='small' className='text-primary-800'>
								Disabled modules will not appear in the client application sidebar
							</Text>
						</li>
						<li className='flex items-start gap-2'>
							<span className='text-primary-600 mt-0.5'>•</span>
							<Text variant='small' className='text-primary-800'>
								Existing data is preserved when modules are disabled
							</Text>
						</li>
						<li className='flex items-start gap-2'>
							<span className='text-primary-600 mt-0.5'>•</span>
							<Text variant='small' className='text-primary-800'>
								Users with direct links can still access disabled modules
							</Text>
						</li>
					</ul>
				</section>
			</div>

			{/* Status Reports Section */}
			<section className='bg-white border border-neutral-200 rounded-lg shadow-sm'>
				<div className='p-6 border-b border-neutral-200 flex items-start justify-between gap-4'>
					<div>
						<Text variant='large' weight='semibold'>
							Status Reports
						</Text>
						<Text variant='small' className='text-neutral-600 mt-1'>
							Control which status reports are visible to Cycle Admins in the client application.
						</Text>
					</div>
					<div className='flex gap-2 flex-shrink-0'>
						<Button variant='secondary' size='small' onClick={() => setDisabledReportIds([])}>
							Enable All
						</Button>
						<Button
							variant='secondary'
							size='small'
							onClick={() =>
								setDisabledReportIds(
									Object.values(REPORT_TYPES)
										.flat()
										.map((r) => r.id),
								)
							}>
							Disable All
						</Button>
					</div>
				</div>

				<div className='p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'>
					{Object.entries(REPORT_TYPES).map(([category, reports]) => {
						const reportIds = reports.map((r) => r.id)
						const allEnabled = reportIds.every((id) => !disabledReportIds.includes(id))

						return (
							<div key={category} className='bg-neutral-50 border border-neutral-200 rounded-lg overflow-hidden'>
								<div className='flex items-center justify-between px-4 py-3 border-b border-neutral-200'>
									<Text variant='small' weight='semibold' className='text-neutral-800'>
										{category}
									</Text>
									<button
										type='button'
										className='text-xs text-primary-600 hover:text-primary-800 font-medium transition-colors'
										onClick={() => {
											if (allEnabled) {
												setDisabledReportIds((prev) => [...new Set([...prev, ...reportIds])])
											} else {
												setDisabledReportIds((prev) => prev.filter((id) => !reportIds.includes(id)))
											}
										}}>
										{allEnabled ? 'Disable all' : 'Enable all'}
									</button>
								</div>
								<div className='p-4 space-y-2'>
									{reports.map((report) => (
										<Checkbox
											key={report.id}
											label={report.title}
											checked={!disabledReportIds.includes(report.id)}
											onChange={(e) => handleReportToggle(report.id, e.target.checked)}
										/>
									))}
								</div>
							</div>
						)
					})}
				</div>

				<div className='p-6 border-t border-neutral-200 flex justify-end gap-3'>
					<Button
						variant='secondary'
						onClick={() => setDisabledReportIds(savedDisabledReportIds ?? [])}
						disabled={!hasReportChanges}>
						Reset
					</Button>
					<Button variant='primary' onClick={handleSaveReports} disabled={!hasReportChanges || isSavingReports}>
						{isSavingReports ? 'Saving...' : 'Save Changes'}
					</Button>
				</div>
			</section>

			{/* Flagged Words Section */}
			<section className='mt-8 bg-white border border-neutral-200 rounded-lg shadow-sm'>
				<div className='p-6 border-b border-neutral-200'>
					<Text variant='large' weight='semibold'>
						Flagged Words
					</Text>
					<Text variant='small' className='text-neutral-600 mt-1'>
						Upload a CSV file to configure flagged words for evaluation comment monitoring
					</Text>
				</div>
				<FlaggedWordsUpload />
			</section>

			{/* DealTeam Configuration Modal */}
			<ConfigureDealTeamModal
				isOpen={isDealTeamModalOpen}
				onClose={() => setIsDealTeamModalOpen(false)}
				organizationId={organizationId as string}
				initialTab={searchParams.get('crm_connection_callback') ? 'connections' : 'integrations'}
			/>

			<Dialog
				isOpen={isBlocked}
				onClose={cancelLeave}
				onConfirm={confirmLeave}
				title='Unsaved Changes'
				message='You have unsaved changes that will be lost if you leave this page. Are you sure you want to continue?'
				type='confirm'
				confirmLabel='Leave'
			/>
		</main>
	)
}
